import {
  IconAnalytics,
  IconBell,
  IconChainLinkGray,
  IconChartBar,
  IconChartBarGray,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconLayers,
  IconLocation,
  IconPenSquare,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconShareGray,
  IconWarning,
} from '@assets/images/icon';
import { rtlsSystem3 } from '@assets/images/solution';
import video from '@src/assets/images/solution/retailExhibition/video/rtlsVideo.mp4';

import subVideo1 from '@src/assets/images/solution/retailExhibition/video/sub/rtlsVideo1.mp4';
import subVideo2 from '@src/assets/images/solution/retailExhibition/video/sub/rtlsVideo2.mp4';
import subVideo3 from '@src/assets/images/solution/retailExhibition/video/sub/rtlsVideo3.mp4';

import image1 from '@src/assets/images/solution/retailExhibition/img/1.avif';
import image2 from '@src/assets/images/solution/retailExhibition/img/2.avif';
import image3 from '@src/assets/images/solution/retailExhibition/img/3.avif';
import image4 from '@src/assets/images/solution/retailExhibition/img/4.avif';
import image5 from '@src/assets/images/solution/retailExhibition/img/5.avif';

export default {
  type: 'Retail Solution',

  banner: {
    title: 'Track visitor locations and automate exhibit guidance.',
    desc: 'Track visitors in real time using wearable UWB tags and provide personalized exhibit information automatically when they approach an artwork, enhancing the viewing experience.',
  },

  video: {
    title: 'When you stand in front of an artwork, guidance starts automatically.',
    desc: 'As visitors approach an exhibit, explanations play automatically. Without the need for a guide, visitors can instantly understand artworks, enjoying a more immersive and convenient museum experience.',
    play: video,
  },

  func: [
    {
      theme: 'Automatically provide exhibit guidance based on visitor location.',
      title: 'Real-time visitor location tracking',
      desc: 'Track visitor location with a wearable necklace-type UWB tag with ±20cm accuracy. The system detects when a visitor nears an artwork and instantly starts guidance.',
      sub1: 'Real-time status monitoring',
      icon1: IconClockGray,
      sub2: 'Fast data integration',
      icon2: IconShareGray,
      video: subVideo1,
    },
    {
      title: 'Automatic personalized guidance near artworks',
      desc: 'When a visitor approaches a specific artwork, the explanation starts automatically. Visitors can get in-depth information without any additional actions.',
      sub1: 'Provide statistical data',
      icon1: IconChartBarGray,
      sub2: 'High accuracy',
      icon2: IconRulerGray,
      image: image1,
    },
    {
      title: 'Analyze movement paths using visitor badges',
      desc: 'Analyze visitor movement paths and dwell data using wearable badges to understand true interests. This helps maximize exhibit planning and effectiveness.',
      sub1: 'High accuracy',
      icon1: IconRulerGray,
      sub2: 'Fast data integration',
      icon2: IconChainLinkGray,
      image: image2,
    },
    {
      title: 'Instant alert when entering restricted areas',
      desc: 'If a visitor crosses a fence or enters a restricted area, an alert is immediately sent to the manager. Real-time monitoring prevents potential safety incidents.',
      sub1: 'High accuracy',
      icon1: IconRulerGray,
      sub2: 'Fast data integration',
      icon2: IconChainLinkGray,
      video: subVideo3,
    },
    {
      theme: 'Use visitor data to efficiently manage exhibit operations.',
      title: 'Automatic interest analysis by artwork',
      desc: 'Visualize areas with the longest visitor stays using heatmaps to intuitively analyze space occupancy. Identify popular zones and exhibits clearly.',
      sub1: 'Provide statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image3,
    },
    {
      title: 'Data-driven content planning support',
      desc: 'Analyze visitor movement and dwell data for practical use in future exhibit planning. Reflect actual interests to maximize exhibit impact.',
      sub1: 'Provide statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      video: subVideo2,
    },
    {
      title: 'Automatic analysis of store product preferences',
      desc: 'Analyze visitor dwell times and movement paths in the gift store to objectively measure product popularity. Use this insight to optimize product mix and boost sales.',
      sub1: 'Provide statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image4,
    },
    {
      title: 'Support for optimized space layout',
      desc: 'Use visitor congestion data to deploy staff in real time, maximize workforce efficiency, and reduce operating costs.',
      sub1: 'Provide statistical data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image5,
    },
  ],

  subfunc: {
    title: 'Key Features Overview',
    desc: '',

    data: [
      {
        icon: IconLocation,
        title: 'Visitor location tracking',
        desc: 'Track visitor badges in real time to monitor location.',
      },
      {
        icon: IconBell,
        title: 'Automatic exhibit guidance',
        desc: 'When visitors approach an artwork, personalized explanations are automatically played.',
      },
      {
        icon: IconRoute,
        title: 'Movement path recording',
        desc: 'Automatically record and analyze visitor movement paths and dwell times by artwork.',
      },
      {
        icon: IconWarning,
        title: 'Restricted area entry alerts',
        desc: 'Send immediate alerts to managers when visitors enter restricted zones.',
      },
      {
        icon: IconAnalytics,
        title: 'Artwork interest analysis',
        desc: 'Visualize visitor dwell locations and times with heatmaps to analyze interest levels.',
      },
      {
        icon: IconPenSquare,
        title: 'Support for exhibit content planning',
        desc: 'Analyze viewing data to reflect visitor preferences in exhibit planning.',
      },
      {
        icon: IconSearch,
        title: 'Store product preference analysis',
        desc: 'Analyze in-store visitor dwell data to identify popular products.',
      },
      {
        icon: IconDashboard,
        title: 'Real-time staff deployment optimization',
        desc: 'Analyze visitor congestion in real time to support efficient staff deployment.',
      },
      {
        icon: IconLayers,
        title: 'Visitor revisit rate measurement',
        desc: 'Analyze revisit frequency and behavior patterns to assess visitor loyalty.',
      },
      {
        icon: IconChartBar,
        title: 'Event performance measurement',
        desc: 'Automatically collect pre- and post-event visitor data to evaluate event performance.',
      },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem3,
  },

  productTitle: 'Product Packages',
  product: [
    {
      tag: 'peoplePackage',
    },
    {
      tag: 'assetPackage',
    },
    {
      tag: 'workerPackage',
    },
  ],

  contents: [
    {
      data: 'manufacturingAsset',
    },
    {
      data: 'logisticsSafety',
    },
    {
      data: 'blogMedical',
    },
    {
      data: 'blogRTLS',
    },
  ],
};
